import { formatToTitleCase } from './format-to-title-case';

import { SchedulesSearchPartnerFacility } from '@/app/(main)/(pages)/busca/[slug]/_types/search-partner-data.type';

export type AddressFormatterType
  = | 'basic'
    | 'full'
    | 'cityAndState'
    | 'cityAndNeighborhood'
    | 'onlyNeighborhood'
    | 'forSearchMaps'
    | 'cityAndNeighborhoodAndState'
    | 'fullWithoutZipcode';

export const addressFormatter = (
  facility: Partial<SchedulesSearchPartnerFacility>,
  type: AddressFormatterType = 'basic',
): string => {
  if (!facility) {
    return '';
  }

  const { addressLine1, addressLine2, number, neighborhood, city, state, zipcode } = facility;

  if (type === 'cityAndState') {
    return `${city} - ${state}`;
  }

  if (type === 'cityAndNeighborhood') {
    return `${city} - ${formatToTitleCase(neighborhood || '')}`;
  }

  if (type === 'cityAndNeighborhoodAndState') {
    return `${formatToTitleCase(neighborhood || '')}, ${city} - ${state}`;
  }

  if (type === 'onlyNeighborhood') {
    return `${formatToTitleCase(neighborhood || '')}`;
  }

  if (type === 'basic') {
    return `${formatToTitleCase(addressLine1 || '')}, ${number || ''} - ${formatToTitleCase(neighborhood || '')}`;
  }

  if (type === 'full') {
    const formattedZipcode = zipcode?.replace(/(\d{5})(\d{3})/, '$1-$2') || '';
    return `${formatToTitleCase(addressLine1 || '')}, ${number || ''}${
      addressLine2 ? `, ${formatToTitleCase(addressLine2)}` : ''
    }, ${formatToTitleCase(neighborhood || '')}, ${city} - ${state}, ${formattedZipcode}`;
  }

  if (type === 'fullWithoutZipcode') {
    return `${formatToTitleCase(addressLine1 || '')}, ${number || ''}${
      addressLine2 ? `, ${formatToTitleCase(addressLine2)}` : ''
    }, ${formatToTitleCase(neighborhood || '')}, ${city} - ${state}`;
  }

  if (type === 'forSearchMaps') {
    const formattedZipcode = zipcode?.replace(/(\d{5})(\d{3})/, '$1-$2') || '';
    return `${formatToTitleCase(addressLine1 || '')}, ${number || ''} - ${formatToTitleCase(
      neighborhood || '',
    )}, ${city} - ${state}, ${formattedZipcode}`;
  }

  return '';
};

export interface FormatAddressParams {
  addressLine1?: string;
  number?: string | number;
  addressLine2?: string;
  neighborhood?: string;
  city?: string;
  state?: string;
  zipcode?: string;
}

export function formatGoogleMapsAddress({
  addressLine1,
  number,
  addressLine2,
  neighborhood,
  city,
  state,
  zipcode,
}: FormatAddressParams): string {
  const formattedNumber = String(number) === '0' ? undefined : number;

  return [
    addressLine1,
    formattedNumber,
    addressLine2,
    neighborhood,
    city,
    state,
    zipcode,
  ]
    .filter((value) => value !== undefined && value !== null && value !== '')
    .join(', ');
}

export function formatAddress({
  addressLine1,
  number,
  addressLine2,
  neighborhood,
  city,
  state,
}: FormatAddressParams): string {
  const normalizedNumber = number && String(number) !== '0' ? number : null;

  const base = [
    addressLine1 && formatToTitleCase(addressLine1),
    normalizedNumber,
    addressLine2 && formatToTitleCase(addressLine2),
    neighborhood && formatToTitleCase(neighborhood),
  ]
    .filter(Boolean)
    .join(', ');

  const location = [city, state].filter(Boolean).join(' - ');

  return [base, location].filter(Boolean).join(', ');
}

export function formatSimpleAddress({ neighborhood, city, state }: FormatAddressParams): string {
  const location = [city, state].filter(Boolean).join(' - ');
  return [neighborhood && formatToTitleCase(neighborhood), location].filter(Boolean).join(', ');
}
